import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Chart } from 'react-chartjs-2';

import { DaySummary, fillDays } from '../data/period_insights';
import { DS, formatSigned } from '../shared/Dashboard';
import { getTooltipDefaults } from './helpers';

/**
 * End-of-day rating as a line over each day's net rating change. Days without
 * rounds keep their slot so gaps in play are visible.
 */
const RatingAndRoundsByDayChart: React.FC<{ days: DaySummary[] }> = ({ days }) => {
    const filled = useMemo(() => fillDays(days), [days]);
    const changeColor = (d: { summary?: DaySummary }, a: number) =>
        alpha(d.summary && d.summary.totalChange < 0 ? DS.loss : DS.gain, a);

    return (
        <Box sx={{ height: { xs: 260, md: 320 } }}>
            <Chart
                type="bar"
                data={{
                    labels: filled.map(d => d.date.format('MMM D')),
                    datasets: [
                        {
                            type: 'line' as const,
                            label: 'Rating',
                            data: filled.map(d => d.summary?.endRating ?? null),
                            spanGaps: true,
                            borderColor: DS.brand,
                            backgroundColor: DS.brand,
                            borderWidth: 2,
                            pointRadius: filled.length > 60 ? 0 : 3,
                            pointHoverRadius: 5,
                            tension: 0.3,
                            yAxisID: 'y',
                            order: 1,
                        },
                        {
                            type: 'bar' as const,
                            label: 'Rating change',
                            data: filled.map(d => d.summary?.totalChange ?? null),
                            backgroundColor: filled.map(d => changeColor(d, 0.35)),
                            hoverBackgroundColor: filled.map(d => changeColor(d, 0.6)),
                            borderRadius: 4,
                            borderSkipped: false,
                            maxBarThickness: 18,
                            yAxisID: 'y1',
                            order: 2,
                        },
                    ],
                }}
                options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    animation: { duration: 500 },
                    interaction: { mode: 'index', intersect: false },
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            ...getTooltipDefaults({ palette: { text: { primary: DS.text, secondary: DS.textMuted }, background: { default: '#161c24' } } }),
                            padding: 12,
                            borderColor: DS.borderStrong,
                            borderWidth: 1,
                            displayColors: false,
                            callbacks: {
                                title: (items) => filled[items[0].dataIndex].date.format('dddd, MMM D'),
                                label: (item) => {
                                    const d = filled[item.dataIndex].summary;
                                    if (!d) return 'No rounds';
                                    return [
                                        `Ended at ${d.endRating} (${formatSigned(d.totalChange)})`,
                                        `${d.count} round${d.count !== 1 ? 's' : ''} · gained in ${d.gainPercent}%`,
                                    ];
                                },
                            },
                            filter: (item) => item.datasetIndex === 1,
                        },
                    },
                    scales: {
                        x: {
                            border: { display: false },
                            grid: { display: false },
                            ticks: { color: DS.textMuted, font: { size: 11 }, maxTicksLimit: 10, maxRotation: 0 },
                        },
                        y: {
                            position: 'left',
                            border: { display: false },
                            grid: { color: 'rgba(145,158,171,0.08)' },
                            ticks: { color: DS.textMuted, font: { size: 11 }, maxTicksLimit: 6 },
                            title: { display: true, text: 'Rating', color: DS.textMuted, font: { size: 11 } },
                        },
                        y1: {
                            position: 'right',
                            border: { display: false },
                            // Only the zero line, so gains and losses read against it
                            grid: { drawOnChartArea: true, color: (ctx) => (ctx.tick?.value === 0 ? DS.borderStrong : 'transparent') },
                            ticks: { color: DS.textFaint, font: { size: 11 }, precision: 0, maxTicksLimit: 5, callback: (v) => formatSigned(Number(v)) },
                            title: { display: true, text: 'Rating change', color: DS.textFaint, font: { size: 11 } },
                        },
                    },
                }}
            />
        </Box>
    );
};

export default RatingAndRoundsByDayChart;
